import anime from 'animejs';
import LocomotiveScroll from 'locomotive-scroll';

export const SCROLL_DURATION = 1;
export const scrollEasing = (t) => 1 - Math.pow(1 - t, 3);

function scrollAnimations(calls, way, target) {
    calls.forEach((call) => {
        if (call === 'setFaqCategory') {
            const category = target.getAttribute('data-category');
            const link = document.querySelector('.faqCategories a[data-category="' + category + '"]');

            if (link) {
                if (way === 'enter') {
                    link.classList.add('active');
                } else {
                    link.classList.remove('active');
                }
            }
        }

        if (call === 'toggleVideo') {
            const videoPlayer = target.querySelector('video');
            if (videoPlayer && videoPlayer.getAttribute('src') !== '') {
                if (way === 'enter') {
                    videoPlayer.play();
                } else {
                    videoPlayer.pause();
                }
            }
        }

        if (call === 'timelineImages') {
            const stack = target.querySelector('.timeline_images_stack');
            if (!stack) return;
            const items = stack.querySelectorAll('.timeline_images_stack_item');
            if (stack && items.length > 0 && !stack.classList.contains('animated')) {
                stack.classList.add('animated');
                if (way === 'enter') {
                    anime({
                        targets: items,
                        scale: [0, 1],
                        translateX() {
                            return anime.random(-30, 30);
                        },
                        translateY() {
                            return anime.random(-30, 30);
                        },
                        rotate() {
                            return anime.random(-10, 10);
                        },
                        easing: 'easeOutElastic(1, .6)',
                        duration: 1000,
                        delay: anime.stagger(200),
                    });
                }
            }
        }
    });
}

class ScrollService {
    constructor() {
        this.instance = null;
        this.callbacks = new Set();
        this.currentValues = {
            scroll: 0,
            limit: 0,
            velocity: 0,
            direction: 0,
            progress: 0,
        };
    }

    _dispatch(values = {}) {
        this.currentValues = {
            scroll: typeof values.scroll === 'number' ? values.scroll : this.currentValues.scroll,
            limit: typeof values.limit === 'number' ? values.limit : this.currentValues.limit,
            velocity: typeof values.velocity === 'number' ? values.velocity : this.currentValues.velocity,
            direction: typeof values.direction === 'number' ? values.direction : this.currentValues.direction,
            progress: typeof values.progress === 'number' ? values.progress : this.currentValues.progress,
        };

        this.callbacks.forEach((callback) => {
            callback(this.currentValues);
        });
    }

    _bindScrollCallEvents() {
        if (!this.instance) return;
        this.instance.on('call', (value, way, obj) => {
            const calls = Array.isArray(value) ? value : [value];
            const target = obj?.el ?? null;
            if (!target) return;
            scrollAnimations(calls, way, target);
        });
    }

    create() {
        this.instance = new LocomotiveScroll({
            scrollCallback: (values) => {
                this._dispatch(values);
            },
        });

        if (history.scrollRestoration) {
            history.scrollRestoration = 'manual';
            window.scrollTo(0, 0);
        }

        this._bindScrollCallEvents();
        this._dispatch();
    }

    getInstance() {
        return this.instance;
    }

    getCurrentValues() {
        return this.currentValues;
    }

    subscribe(callback, runImmediately = true) {
        if (typeof callback !== 'function') return () => {};

        this.callbacks.add(callback);

        if (runImmediately) {
            callback(this.currentValues);
        }

        return () => {
            this.callbacks.delete(callback);
        };
    }

    resize() {
        if (this.instance && typeof this.instance.resize === 'function') {
            this.instance.resize();
        }
    }

    refresh(container) {
        if (!this.instance) return;

        const runUpdate = () => {
            // Locomotive Scroll v4 (projet actuel) : rescanne [data-scroll] et applique is-inview
            if (typeof this.instance.update === 'function') {
                this.instance.update();
            } else if (
                container instanceof Element
                && typeof this.instance.removeScrollElements === 'function'
                && typeof this.instance.addScrollElements === 'function'
            ) {
                // Locomotive Scroll v5+
                this.instance.removeScrollElements(container);
                this.instance.addScrollElements(container);
            }

            this.resize();
        };

        // Laisser le DOM AJAX se peindre avant de recalculer les positions d’animation
        requestAnimationFrame(() => {
            requestAnimationFrame(runUpdate);
        });
    }

    getY() {
        if (typeof this.currentValues.scroll === 'number') {
            return this.currentValues.scroll;
        }

        if (this.instance?.lenisInstance && typeof this.instance.lenisInstance.scroll === 'number') {
            return this.instance.lenisInstance.scroll;
        }

        return window.scrollY;
    }

    scrollTop(force) {
        if (!this.instance) return;
        if (force) {
            this.instance.scrollTo(0, { immediate: true, force: true });
        } else {
            this.instance.scrollTo(0, { duration: SCROLL_DURATION, easing: scrollEasing, force: true });
        }
    }

    scrollTo(elt, offset = 0) {
        if (!this.instance) return;

        this.instance.scrollTo(elt, { duration: SCROLL_DURATION, easing: scrollEasing, offset, force: true });
    }

    stop() {
        if (this.instance && typeof this.instance.stop === 'function') {
            this.instance.stop();
        }
    }

    start() {
        if (this.instance && typeof this.instance.start === 'function') {
            this.instance.start();
        }
    }
}

const scrollService = new ScrollService();

export default scrollService;

export function createScroll() {
    scrollService.create();
}

export function subscribeToScroll(callback, runImmediately = true) {
    return scrollService.subscribe(callback, runImmediately);
}

export function resizeScroll() {
    scrollService.resize();
}

export function refreshScroll(container) {
    scrollService.refresh(container);
}

export function getScrollY() {
    return scrollService.getY();
}

export function scrollTo(elt, offset = 0) {
    scrollService.scrollTo(elt, offset);
}

export function scrollTop(force) {
    scrollService.scrollTop(force);
}

export function getScrollInstance() {
    return scrollService.getInstance();
}

export function getCurrentScrollValues() {
    return scrollService.getCurrentValues();
}
